import React from 'react';
import { FormSpy } from 'react-final-form';
import { useOutletContext } from 'react-router-dom';

import {
  ENCOUNTERED_PROBLEMS,
  IDENTITY_PROVIDERS,
  inputAcceptTransmitDataConfig,
  inputEmailConfig,
  inputEmailValidConfig,
  inputEncounterdProblemConfig,
  inputFileUploadConfig,
  inputIdentityProviderConfig,
  inputServiceProviderConfig,
  inputTextareaDescriptionConfig,
} from '../../../configs';
import { ContactFormValues, PageLayoutContext } from '../../../interfaces';
import { FileInputLabel, FormName, FormType } from '../../../libs/enums';
import { getIdpLabelByValue } from '../../../libs/helpers';
import {
  CheckboxComponent,
  FileUploadComponent,
  SelectComponent,
  TextAreaComponent,
  TextInputComponent,
} from '../../dsfr';
import { FormWrapperComponent } from '../form-wrapper';
import { ReadOnlyComponent } from '../read-only';

export const ContactForm = React.memo(() => {
  const { encounteredProblem, errorId, idpName, platform, spName } =
    useOutletContext<PageLayoutContext>();

  const identityProvider = getIdpLabelByValue(idpName);
  const serviceProvider = (!!spName && spName) || undefined;

  const memoizedDescription = React.useMemo(
    () => (
      <React.Fragment>
        Pour faciliter la prise en charge de votre demande, veuillez nous transmettre toute pièce
        justificative que vous jugez importante pour le traitement de votre demande (ex. erreur
        affichée, compte utilisé, ...).
        <br />
        <br />
        Taille maximale : 2Mo. Les formats supportés sont : JPG, JPEG, PNG, GIF, DOCX, ODT, PDF.
      </React.Fragment>
    ),
    [],
  );

  return (
    <FormWrapperComponent<ContactFormValues>
      initialValues={{
        acceptTransmitData: undefined,
        email: undefined,
        emailValid: undefined,
        encounteredProblem,
        errorId,
        fileUpload: undefined,
        formName: FormName.CONTACT,
        formType: FormType.CONTACT,
        identityProvider,
        platform,
        serviceProvider,
        textareaDescription: undefined,
      }}>
      <React.Fragment>
        <div className="fr-grid-row fr-grid-row--gutters fr-mb-3w">
          <div className="fr-col-12 fr-col-sm-6">
            {encounteredProblem ? (
              <ReadOnlyComponent {...inputEncounterdProblemConfig} />
            ) : (
              <SelectComponent {...inputEncounterdProblemConfig} values={ENCOUNTERED_PROBLEMS} />
            )}
          </div>
          <div className="fr-col-12 fr-col-sm-6">
            {identityProvider ? (
              <ReadOnlyComponent {...inputIdentityProviderConfig} />
            ) : (
              <SelectComponent {...inputIdentityProviderConfig} values={IDENTITY_PROVIDERS} />
            )}
          </div>
        </div>
        <TextInputComponent {...inputServiceProviderConfig} />
        <TextInputComponent {...inputEmailConfig} />
        <TextInputComponent {...inputEmailValidConfig} />
        <TextAreaComponent
          {...inputTextareaDescriptionConfig}
          description="Merci de nous préciser la démarche que vous souhaitez effectuer."
        />
        <FormSpy subscription={{ values: true }}>
          {(props: { values: ContactFormValues }) => {
            const idpService = getIdpLabelByValue(props.values.identityProvider);
            const label = !idpService
              ? FileInputLabel.PJ
              : `${FileInputLabel.PROFIL} ${idpService}`;
            return (
              <FileUploadComponent
                {...inputFileUploadConfig}
                description={memoizedDescription}
                label={label}
                required={false}
              />
            );
          }}
        </FormSpy>
        <CheckboxComponent {...inputAcceptTransmitDataConfig} />
      </React.Fragment>
    </FormWrapperComponent>
  );
});

ContactForm.displayName = 'ContactForm';
